import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { mdiPencil } from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { JSX } from "react";

import type { InputProps } from "./types";

type TextInputInlineProps = Pick<
  InputProps,
  | "name"
  | "defaultValue"
  | "className"
  | "control"
  | "isRequired"
  | "errorMessage"
>;

interface TextInputInlineFieldProps {
  inputId: string;
  inputClassnames: string;
  invisibleSpanClassnames: string;
  onChange: (...event: unknown[]) => void;
  onBlur: () => void;
  value: string;
  error?: { message?: string };
  onFocusClick: () => void;
}

function TextInputInlineField({
  inputId,
  inputClassnames,
  invisibleSpanClassnames,
  onChange,
  onBlur,
  value,
  error,
  onFocusClick,
}: TextInputInlineFieldProps): JSX.Element {
  const [inputWidth, setInputWidth] = useState(0);
  const spanRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const newInputWidth = spanRef.current?.offsetWidth ?? 1;
    setInputWidth(newInputWidth);
  }, [value]);

  useEffect(() => {
    const spanElement = spanRef.current;
    const resizeObserver = new ResizeObserver(() => {
      const newInputWidth = spanElement?.offsetWidth ?? 1;
      setInputWidth(newInputWidth);
    });

    if (spanElement) {
      resizeObserver.observe(spanElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="flex items-center justify-center gap-[0.46875rem]">
      <div className="relative">
        <input
          className={inputClassnames}
          id={inputId}
          maxLength={128}
          onBlur={onBlur}
          onChange={onChange}
          style={{ width: inputWidth }}
          type="text"
          value={value.replace(/ +(?= )/g, "")}
        />
        <span ref={spanRef} className={invisibleSpanClassnames}>
          {`${value}.`}
        </span>
      </div>

      <div onClick={onFocusClick}>
        <Icon
          className="text-muted-foreground cursor-pointer"
          path={mdiPencil}
          size={0.9375}
        />
      </div>
      {error ? (
        <span className="text-destructive text-[0.9375rem] whitespace-nowrap">
          {error.message}
        </span>
      ) : null}
    </div>
  );
}

export default function TextInputInline({
  name,
  className,
  defaultValue,
  isRequired,
  control,
  errorMessage,
}: TextInputInlineProps): JSX.Element {
  const inputId = `text-input-inline-${name}`;
  const { t } = useTranslation("editor-errors");

  const inputClassnames = classNames(className, "bg-transparent");

  const invisibleSpanClassnames = classNames(
    "invisible absolute whitespace-nowrap left-0 min-w-px",
    className,
  );

  const handleFocusClick = (): void => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    input?.focus();
  };

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }): JSX.Element => (
        <TextInputInlineField
          error={error}
          inputClassnames={inputClassnames}
          inputId={inputId}
          invisibleSpanClassnames={invisibleSpanClassnames}
          onBlur={onBlur}
          onChange={onChange}
          onFocusClick={handleFocusClick}
          value={value}
        />
      )}
      rules={{
        required: isRequired && (errorMessage ?? t("requiredField")),
      }}
    />
  );
}
